// @flow
import type {Element, Node} from 'react'
import React from 'react'
import classNames from 'classnames'

import {useToggle} from 'utils/hooks'
import ArrowUp from 'components/ui/icons/ios/ArrowUp'
import ArrowDown from 'components/ui/icons/ios/ArrowDown'
import styles from './index.scss'

type ControlledProps = $ReadOnly<{
  children: Node,
  className: ?string,
  showLessTitle: string,
  showMoreTitle: string,
  togglePosition: 'left' | 'right',
  onToggle: (isOpen: boolean) => any,
  isOpen: boolean,
}>

type UncontrolledProps = $ReadOnly<{
  children: Node,
  className: ?string,
  showLessTitle: string,
  showMoreTitle: string,
  togglePosition: 'left' | 'right',
}>

// This is a hack to mitigate I18n module loading issues.
const defaultShowLessTitle = () => I18n.t('shared.show_less')
const defaultShowMoreTitle = () => I18n.t('shared.show_more')

function ShowMore({
  children,
  className = null,
  showLessTitle = defaultShowLessTitle(),
  showMoreTitle = defaultShowMoreTitle(),
  togglePosition = 'left',
}: UncontrolledProps): Node {
  const [isOpen, {toggle}] = useToggle(false)

  return (
    <ControlledShowMore
      className={className}
      onToggle={toggle}
      showMoreTitle={showMoreTitle}
      showLessTitle={showLessTitle}
      togglePosition={togglePosition}
      isOpen={isOpen}
    >
      {children}
    </ControlledShowMore>
  )
}

function ControlledShowMore({
  children,
  className = null,
  onToggle = () => {},
  showLessTitle = defaultShowLessTitle(),
  showMoreTitle = defaultShowMoreTitle(),
  togglePosition = 'left',
  isOpen = false,
}: ControlledProps): Element<"div"> {
  const toggleClass = classNames(styles.button, {
    [styles.buttonRight]: togglePosition === 'right',
    [styles.buttonActive]: isOpen,
  })

  return (
    <div className={className}>
      <button type="button" className={toggleClass} onClick={onToggle}>
        <span test-id="show-more">
          {isOpen
            ? showLessTitle
            : showMoreTitle
          }
        </span>
        <div className={styles.icon}>
          {isOpen ? <ArrowUp /> : <ArrowDown />}
        </div>
      </button>
      {isOpen && (
        <div className={styles.content}>
          {children}
        </div>
      )}
    </div>
  )
}

export {ControlledShowMore}

export default ShowMore
